import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "../i18n/translations";
import { getContent } from "../services/firestore";
import { api } from "../lib/api";

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

const SYMBOL = { AZN: "₼", USD: "$", EUR: "€", RUB: "₽" };
const FALLBACK_RATES = { AZN: 1, USD: 0.59, EUR: 0.54, RUB: 52 };

// Fallback site content if Firestore content/site doc doesn't exist yet.
// `banners` MUST start as an empty array — otherwise on every page refresh a
// stock vinyl image would flash for a few hundred ms before Firestore replies,
// even when the admin has not configured any banner.
const DEFAULT_CONTENT = {
  hero: {
    eyebrow: { az: "PREMİUM SEÇİM — BAKI, AZ", en: "PREMIUM SELECTION — BAKU, AZ", ru: "ПРЕМИУМ ВЫБОР — БАКУ, AZ" },
    title: { az: "Analoq ruh, rəqəmsal dünyada.", en: "Analog soul in a digital world.", ru: "Аналоговая душа в цифровом мире." },
    desc: { az: "Bakı audiofil cəmiyyəti üçün ən nadir vinil presləri.", en: "Curating the finest vinyl pressings for the Baku audiophile community.", ru: "Лучшие виниловые пластинки для аудиофилов Баку." },
  },
  banners: [],
  editorial_blocks: [],
  ambient_player: { title: {}, artist: {}, src: "" },
  featured_artist_id: "",
  discography_artist_ids: [],
  footer: {
    about: { az: "Bakı audiofilləri üçün vinil şedevrlər 2019-cu ildən. Rəqəmsal dünyada analoq ruhu.", en: "Curating the finest vinyl pressings since 2019. Analog soul in a digital world.", ru: "Лучшие виниловые пластинки с 2019 года." },
    address: { az: "28 May küçəsi\nBakı AZ1010\nÇər-Baz 12:00–22:00", en: "28 May Street\nBaku AZ1010\nTue–Sun 12:00–22:00", ru: "ул. 28 Мая\nБаку AZ1010\nВт–Вс 12:00–22:00" },
    copyright: { az: "© 2026 VINYL BAKU AZ", en: "© 2026 VINYL BAKU AZ", ru: "© 2026 VINYL BAKU AZ" },
    tagline: { az: "180G • 33⅓ RPM • ÖMÜRBOYU PRESLƏNİB", en: "180G • 33⅓ RPM • PRESSED FOR LIFE", ru: "180G • 33⅓ RPM • НАВЕЧНО" },
  },
};

function pickLang(value, lang) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  return value[lang] || value.en || value.az || Object.values(value)[0] || "";
}

export function AppProvider({ children }) {
  const [lang, setLang] = useState(localStorage.getItem("vbaz_lang") || "az");
  const [currency, setCurrency] = useState(localStorage.getItem("vbaz_currency") || "AZN");
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [ratesMeta, setRatesMeta] = useState({ source: "fallback", fetched_at: null });

  // Hydrate site content from localStorage on first paint so banners, page
  // copy and CMS images render IMMEDIATELY — no white-flash while Firestore
  // round-trips. We still refresh from Firestore in the background and
  // overwrite the cache when the fresh value arrives.
  const [content, setContent] = useState(() => {
    try {
      const raw = localStorage.getItem("vbaz_content_v1");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          return { ...DEFAULT_CONTENT, ...parsed };
        }
      }
    } catch { /* ignore */ }
    return DEFAULT_CONTENT;
  });

  useEffect(() => {
    getContent("site").then((c) => {
      if (c) {
        const merged = { ...DEFAULT_CONTENT, ...c };
        setContent(merged);
        try { localStorage.setItem("vbaz_content_v1", JSON.stringify(c)); } catch { /* quota */ }
      }
    }).catch(() => {});
    // Fetch live exchange rates (cached server-side for 6h)
    api.get("/currency/rates").then((r) => {
      const d = r.data || {};
      if (d.rates && typeof d.rates === "object") {
        setRates({ ...FALLBACK_RATES, ...d.rates });
        setRatesMeta({ source: d.source || "live", fetched_at: d.fetched_at || null });
      }
    }).catch(() => {});
  }, []);

  useEffect(() => { localStorage.setItem("vbaz_lang", lang); }, [lang]);
  useEffect(() => { localStorage.setItem("vbaz_currency", currency); }, [currency]);

  const t = useMemo(() => translations[lang] || translations.en, [lang]);

  const formatPrice = (priceAzn) => {
    const v = ((priceAzn || 0) * (rates[currency] || 1));
    // RUB → no decimals, others → 2 decimals
    const formatted = currency === "RUB" ? Math.round(v).toString() : v.toFixed(2);
    return `${SYMBOL[currency] || ""}${formatted}`;
  };

  const refreshRates = async () => {
    try {
      const r = await api.get("/currency/rates?refresh=true");
      const d = r.data || {};
      if (d.rates) {
        setRates({ ...FALLBACK_RATES, ...d.rates });
        setRatesMeta({ source: d.source || "live", fetched_at: d.fetched_at || null });
      }
      return d;
    } catch (_e) { return null; }
  };

  const tx = (path) => {
    const segs = path.split(".");
    let cur = content;
    for (const s of segs) cur = cur?.[s];
    return pickLang(cur, lang);
  };

  // Read a page-level CMS field (content.pages.<key>.<field>) with fallback.
  // Usage: tp("about", "title", "Default title")
  const tp = (pageKey, fieldKey, fallback = "") => {
    const v = content?.pages?.[pageKey]?.[fieldKey];
    const picked = pickLang(v, lang);
    return picked || fallback;
  };

  // Read a page-level CMS image URL (content.pages.<key>.<field>) with fallback.
  const tpImage = (pageKey, fieldKey, fallback = "") => {
    const v = content?.pages?.[pageKey]?.[fieldKey];
    if (typeof v === "string" && v) return v;
    return fallback;
  };

  // Pick a translated value from a Firestore doc.
  // Looks at `<field>_<lang>` first, then `<field>` (legacy fallback).
  const pickField = (doc, field) => {
    if (!doc) return "";
    if (lang !== "az") {
      const v = doc[`${field}_${lang}`];
      if (v != null && v !== "") return v;
    }
    return doc[field] ?? "";
  };

  const reloadContent = async () => {
    const c = await getContent("site");
    if (c) {
      setContent({ ...DEFAULT_CONTENT, ...c });
      try { localStorage.setItem("vbaz_content_v1", JSON.stringify(c)); } catch { /* quota */ }
    }
  };

  const value = {
    lang, setLang, currency, setCurrency, rates, ratesMeta, refreshRates,
    t, tx, tp, tpImage, pickField, content,
    reloadContent, formatPrice, symbol: SYMBOL[currency] || "₼",
  };
  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}
